import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { DollarSign } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 09/29 (#27 B): wire-in tracking. Josh funds first and requests the share after; the
// position carries funding_status due | partial | received | never_funded (legacy: none).
const WireCell = ({ syn, getAuthHeaders, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', reference: '' });
  const status = syn.funding_status;
  if (!status) return <span className="text-gray-300 text-xs">—</span>;
  const due = syn.funding_due_amount ?? syn.advance_amount ?? 0;
  const received = syn.funding_received_amount || 0;

  const post = async (path, body, ok) => {
    setBusy(true);
    try {
      await axios.post(`${API}/funding-requests/${syn.id}/${path}`, body, { headers: getAuthHeaders() });
      toast.success(ok); setOpen(false); if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-xs" onClick={(e) => e.stopPropagation()}>
      {status === 'received' && <><Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">received</Badge><div className="text-muted-foreground">{formatDate(syn.funding_received_at)} {syn.funding_reference || ''}</div></>}
      {status === 'never_funded' && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">never funded</Badge>}
      {(status === 'due' || status === 'partial') && (
        <>
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{status === 'partial' ? `partial ${formatCurrency(received)} of ${formatCurrency(due)}` : `due ${formatCurrency(due)}`}</Badge>
          <div className="flex gap-1 mt-1">
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { setForm(f => ({ ...f, amount: String(Math.max(0, due - received)) })); setOpen(true); }} data-testid={`wire-received-${syn.id}`}>Received</Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-red-700" onClick={() => { if (window.confirm('Mark this wire as never funded? (You can then delete the position.)')) post('status', { status: 'never_funded' }, 'Marked never funded'); }} disabled={busy}>Never</Button>
          </div>
        </>
      )}
      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Wire received — {syn.syndicator_name}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Due {formatCurrency(due)}{received ? `, received so far ${formatCurrency(received)}` : ''}. Records the receipt on the position only — no ledger row.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
              <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Reference</Label><Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="wire / check #" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button onClick={() => post('received', { date: form.date, amount: parseFloat(form.amount) || null, reference: form.reference }, 'Wire recorded')} disabled={busy}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const SyndicatorList = ({ mergedSyndications, loading, onEditSyndication, dealId, getAuthHeaders, onChanged }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (mergedSyndications.length === 0) {
    return <div className="text-center py-12 text-gray-500">No syndications found for this deal.</div>;
  }

  return (
    <>
      {/* Main Syndication Table */}
      <div className="overflow-x-auto">
        <Table data-testid="syndication-table">
          <TableHeader>
            <TableRow className="bg-blue-50">
              <TableHead>Syndicator</TableHead>
              <TableHead className="text-right">Ownership %</TableHead>
              <TableHead className="text-right">Wire In</TableHead>
              <TableHead>Wire Received</TableHead>
              <TableHead className="text-right">Fee Allocated</TableHead>
              <TableHead className="text-right">Available Fees</TableHead>
              <TableHead className="text-right">Available Balance</TableHead>
              <TableHead className="text-right">Withdrawn</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Report</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mergedSyndications.map((syn) => (
              <TableRow
                key={syn.id}
                className={`cursor-pointer hover:bg-gray-50 ${syn.is_stale ? 'bg-amber-50' : ''}`}
                onClick={() => onEditSyndication(syn)}
                data-testid={`syndication-row-${syn.id}`}
              >
                <TableCell className="font-medium text-blue-600">
                  <div className="flex items-center gap-2">
                    {syn.syndicator_name}
                    {syn.role === 'PARTNER' && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Partner</Badge>
                    )}
                    {syn.role === 'INVESTOR_FEE' && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Investor (entitlement)</Badge>
                    )}
                    {(!syn.role || syn.role === 'INVESTOR' || syn.role === 'SYNDICATOR') && (
                      <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">Investor</Badge>
                    )}
                    {syn.collect_fees && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200" data-testid={`collect-fees-badge-${syn.id}`}>
                        <DollarSign className="h-3 w-3 mr-0.5" />Origination entitlement
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium">
                  {syn.retained_pct ? (
                    // 10/07 (Josh): PFS's own row carries the unsyndicated remainder (display-only).
                    <span data-testid={`ownership-with-retained-${syn.id}`}>
                      {+(syn.ownership_percentage + syn.retained_pct).toFixed(2)}%
                      <span className="block text-[10px] font-normal text-slate-500">
                        {syn.ownership_percentage}% + {syn.retained_pct}% retained (house share)
                      </span>
                    </span>
                  ) : `${syn.ownership_percentage}%`}
                </TableCell>
                <TableCell className="text-right text-blue-600">{formatCurrency(syn.principal_invested)}</TableCell>
                <TableCell><WireCell syn={syn} getAuthHeaders={getAuthHeaders} onChanged={onChanged} /></TableCell>
                <TableCell className="text-right text-indigo-600" data-testid={`fee-allocated-${syn.id}`}>
                  {syn.collect_fees ? formatCurrency(syn.fee_allocated) : <span className="text-gray-300">-</span>}
                </TableCell>
                <TableCell className="text-right text-purple-600">
                  {syn.collect_fees ? formatCurrency(syn.available_fees) : <span className="text-gray-300">-</span>}
                </TableCell>
                <TableCell className="text-right font-semibold text-green-600">{formatCurrency(syn.available_total)}</TableCell>
                <TableCell className="text-right text-orange-600">{formatCurrency(syn.withdrawn)}</TableCell>
                <TableCell className="text-center">
                  {syn.is_stale ? (
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">Stale</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">Current</Badge>
                  )}
                </TableCell>
                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/syndications/syndicator/${syn.syndicator_id}/deal/${dealId}/report`} target="_blank" className="text-blue-600 hover:underline text-xs font-medium" data-testid={`deal-syn-report-${syn.id}`}>Report</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Collection Summary */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">Collection Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Syndicator</TableHead>
                  <TableHead className="text-right">Target Collection</TableHead>
                  <TableHead className="text-right">Running Collection</TableHead>
                  <TableHead className="text-right">Fee Allocated</TableHead>
                  <TableHead className="text-right">Fees Collected</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mergedSyndications.map((syn) => (
                  <TableRow key={`summary-${syn.id}`}>
                    <TableCell className="font-medium">{syn.syndicator_name}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(syn.target_collection)}
                      {syn.retained_target > 0 && (
                        <span className="block text-[10px] text-slate-500">
                          + {formatCurrency(syn.retained_target)} retained ({formatCurrency(syn.retained_advance)} advance)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-green-600">{formatCurrency(syn.running_collection || 0)}</TableCell>
                    <TableCell className="text-right text-indigo-600">
                      {syn.collect_fees ? formatCurrency(syn.fee_allocated || 0) : <span className="text-gray-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-purple-600">
                      {syn.collect_fees ? formatCurrency(syn.fees_collected || 0) : <span className="text-gray-300">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-orange-600">{formatCurrency(syn.balance || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default SyndicatorList;
